import React from "react";

export const ESTADOS_SOLICITUD = {
  nueva: { texto: "Nueva", color: "#5b6472", bg: "#eef1f3" },
  en_cola: { texto: "En cola", color: "var(--tema-primario, #2f6690)", bg: "var(--tema-suave, #dceaf5)" },
  cotizando: { texto: "Cotizando", color: "#fff", bg: "var(--tema-titulo, #0b3c60)" },
  pendiente_aprobacion: { texto: "Esperando aprobación", color: "#b85a0b", bg: "#fde6cf" },
  aprobada: { texto: "Aprobada", color: "#2f7a54", bg: "#dcf0e6" },
  en_proceso_pago: { texto: "Proceso de pago", color: "#0f7c8a", bg: "#d5eef1" },
  pagada: { texto: "Pagada", color: "#1e5e3f", bg: "#c9ead8" },
  en_proceso_compra: { texto: "Proceso de compra", color: "#3d5a99", bg: "#e1e7f5" },
  lista_para_retiro: { texto: "Lista para retiro", color: "#9c7a1a", bg: "#f3ecd0" },
  entregada: { texto: "Recibida", color: "#1e5e3f", bg: "#dcf0e6" },
  rechazada: { texto: "Rechazada", color: "#a3402f", bg: "#f7ded9" },
};

// Mientras la solicitud sigue "en_cola" (que es el único valor real que
// guarda la BD entre "En cola", "Cotizando" y "Pendiente de aprobación"),
// hay que distinguir entre las tres a partir de datos derivados:
//   - sin cotizaciones todavía y compras no la marcó -> "En cola"
//   - con cotizaciones (o compras la marcó "en atención"), pero falta algún
//     ítem por tener ganadora -> "Cotizando"
//   - todos los ítems ya tienen ganadora, esperando que el aprobador decida -> "Pendiente de aprobación"
// Úsalo en cualquier lugar donde se pinte el estado de una solicitud, pasando
// siempre `tienePendienteAprobacion` y `enAtencion` cuando estén disponibles
// (los manda el backend como `tiene_pendiente_aprobacion` y `en_atencion`
// en cada fila de solicitudes).
//   - "Proceso de pago" tampoco existe en la BD: es una solicitud "aprobada"
//     que todavía tiene algún pago pendiente (tiene_pago_pendiente).
//   - "Pagada" tampoco: es una solicitud "aprobada" a la que Pagos ya le
//     registró el pago (tiene_pago_realizado) y no queda lo aprobado esperando
//     pago. Las de crédito (sin pago) se quedan en "Aprobada" hasta que compras
//     inicia el proceso de compra.
export function estadoVisible(
  estado,
  totalCotizaciones = 0,
  tienePendienteAprobacion = false,
  enAtencion = false,
  tienePagoPendiente = false,
  tienePagoRealizado = false
) {
  if (estado === "aprobada") {
    if (tienePagoPendiente) return "en_proceso_pago";
    return tienePagoRealizado ? "pagada" : "aprobada";
  }
  if (estado !== "en_cola") return estado;
  if (tienePendienteAprobacion) return "pendiente_aprobacion";
  return totalCotizaciones > 0 || enAtencion ? "cotizando" : "en_cola";
}

export const ESTADOS_ITEM = {
  nueva: { texto: "Nueva", color: "#5b6472", bg: "#eef1f3" },
  en_cola: { texto: "En cola", color: "#5b6472", bg: "#eef1f3" },
  cotizando: { texto: "Cotizando", color: "#fff", bg: "var(--tema-titulo, #0b3c60)" },
  pendiente_aprobacion: { texto: "Esperando aprobación", color: "#b85a0b", bg: "#fde6cf" },
  validado: { texto: "Listo", color: "#2f7a54", bg: "#dcf0e6" },
  aprobada: { texto: "Aprobada", color: "#2f7a54", bg: "#dcf0e6" },
  en_proceso_pago: { texto: "Proceso de pago", color: "#0f7c8a", bg: "#d5eef1" },
  pagada: { texto: "Pagada", color: "#1e5e3f", bg: "#c9ead8" },
  en_proceso_compra: { texto: "Proceso de compra", color: "#3d5a99", bg: "#e1e7f5" },
  lista_para_retiro: { texto: "Lista para retiro", color: "#9c7a1a", bg: "#f3ecd0" },
  entregada: { texto: "Recibido", color: "#1e5e3f", bg: "#dcf0e6" },
  rechazada: { texto: "Rechazado", color: "#a3402f", bg: "#f7ded9" },
};

// "Entregada" y "Recibida" son el mismo paso visto desde cada lado: el
// personal (compras, aprobador, pagos, administrador) la entregó y el
// solicitante la recibió. El rol se lee de la sesión en cada llamada porque
// puede cambiar al cerrar sesión y entrar con otra cuenta.
function esPersonal() {
  try {
    const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
    return !!usuario && usuario.rol !== "solicitante";
  } catch {
    return false;
  }
}
const ESTADOS_SOLICITUD_PERSONAL = {
  ...ESTADOS_SOLICITUD,
  entregada: { ...ESTADOS_SOLICITUD.entregada, texto: "Entregada" },
};
const ESTADOS_ITEM_PERSONAL = {
  ...ESTADOS_ITEM,
  entregada: { ...ESTADOS_ITEM.entregada, texto: "Entregado" },
};
// Usar estas en las pantallas (en vez de ESTADOS_SOLICITUD / ESTADOS_ITEM
// directo) para que cada quien vea el texto que le corresponde.
export const mapaEstadosSolicitud = () => (esPersonal() ? ESTADOS_SOLICITUD_PERSONAL : ESTADOS_SOLICITUD);
export const mapaEstadosItem = () => (esPersonal() ? ESTADOS_ITEM_PERSONAL : ESTADOS_ITEM);

// Qué estado le corresponde mostrar a UN ítem, para que concuerde con el de
// la solicitud completa en vez de quedarse pegado en un genérico "Listo"
// (o, antes de tener cotización ganadora, en un genérico "En cola"/"Cotizando"
// que no encaja mientras la solicitud sigue "Nueva"). Antes esto se calculaba
// distinto —y mal— en cada pantalla; ahora es una sola función compartida.
// ¿Ya se le entregó al solicitante todo lo aprobado de este producto? (En activos: todas sus unidades.)
export function itemEntregado(item) {
  if (item.es_activo_fijo) {
    return !!item.activos && item.activos.length > 0 && item.activos.every((a) => a.entregado);
  }
  const meta = Number(item.cantidad_aprobada) > 0 ? Number(item.cantidad_aprobada) : item.descartado ? 0 : Number(item.cantidad);
  return meta > 0 && Number(item.cantidad_entregada_total || 0) >= meta;
}

export function estadoItemVisible(item, solicitud) {
  // El gerente decidió que este producto no se compra — queda así sin
  // importar en qué estado técnico se quedó el ítem por dentro.
  // (Si se aprobó una parte, el ítem sigue su camino con lo aprobado: solo se muestra
  // "rechazada" cuando no quedó nada aprobado.)
  if (item.descartado && !(Number(item.cantidad_aprobada) > 0)) return "rechazada";
  // Con una parte aprobada, el ítem sigue el camino de la solicitud aunque por dentro
  // quede "cotizando" esperando el resto de la cantidad.
  const conAprobacionParcial = Number(item.cantidad_aprobada) > 0;
  if (item.estado_item !== "validado" && !conAprobacionParcial) {
    if (solicitud.estado === "nueva") return "nueva";
    // Defensa ante un dato mal guardado: un ítem no puede estar realmente
    // "cotizando" si la solicitud no tiene ninguna cotización registrada
    // todavía NI compras la marcó como que ya está trabajando en ella.
    if (item.estado_item === "cotizando" && !solicitud.total_cotizaciones && !solicitud.en_atencion) {
      return "en_cola";
    }
    return item.estado_item;
  }
  // Ya se le entregó todo lo aprobado de ESTE producto: ese es su estado final,
  // aunque la solicitud siga en trámite por otros productos.
  if (itemEntregado(item) && (item.aprobado || conAprobacionParcial)) return "entregada";
  // Ya tiene cotización ganadora — mientras la solicitud siga "en_cola", lo
  // que importa es si ESTE ítem en particular ya lo aprobó el gerente o no
  // (no el estado general de la solicitud, que puede seguir "en_cola" por
  // culpa de otro ítem distinto).
  if (solicitud.estado === "en_cola") {
    // Producto ya aprobado mientras el resto de la solicitud sigue en trámite: se ve su propio avance
    // (Aprobada → Proceso de pago → Pagada), no "Listo".
    if (item.aprobado || conAprobacionParcial) {
      if (item.listo_retiro) return "lista_para_retiro";
      if (item.pago_realizado) return "pagada";
      if (item.pago_en_proceso) return "en_proceso_pago";
      return "aprobada";
    }
    return "pendiente_aprobacion";
  }
  // Un ítem de contado cuyo pago sigue pendiente se ve "Proceso de pago"; los
  // de crédito no esperan nada y siguen a la solicitud (proceso de compra…).
  if (item.pago_en_proceso && ["aprobada", "en_proceso_compra", "lista_para_retiro"].includes(solicitud.estado)) {
    return "en_proceso_pago";
  }
  // Ya se notificó que ESTE producto está listo para retirar: lo dice él solo, sin esperar a los de otros proveedores.
  if (item.listo_retiro && ["aprobada", "en_proceso_compra", "lista_para_retiro"].includes(solicitud.estado)) {
    return "lista_para_retiro";
  }
  // Ya se pagó y compras todavía no inicia el proceso de compra: "Pagada".
  if (item.pago_realizado && solicitud.estado === "aprobada") return "pagada";
  const SIGUE_A_LA_SOLICITUD = ["aprobada", "en_proceso_compra", "lista_para_retiro", "entregada"];
  return SIGUE_A_LA_SOLICITUD.includes(solicitud.estado) ? solicitud.estado : "validado";
}

// Forma en que se pagará una cotización (crédito, cheque, efectivo, contra-entrega).
export const FORMAS_PAGO = [
  { valor: "credito", texto: "Crédito", color: "var(--tema-primario, #2f6690)", bg: "var(--tema-suave, #dceaf5)" },
  { valor: "cheque", texto: "Cheque", color: "#3d5a99", bg: "#e1e7f5" },
  { valor: "efectivo", texto: "Efectivo", color: "#9c7a1a", bg: "#f3ecd0" },
  { valor: "transferencia", texto: "Transferencia", color: "#0f7c8a", bg: "#d5eef1" },
  { valor: "tarjeta", texto: "Tarjeta", color: "#7a4a9c", bg: "#ece1f5" },
];

// Algunos proveedores cotizan en córdobas y otros en dólares. Cada cotización
// se guarda en su moneda original tal como la mandó el proveedor; cuando es
// USD, se guarda también el tipo de cambio usado para poder comparar/sumar
// todo en una sola moneda de referencia (córdobas).
export const MONEDAS = [
  { valor: "NIO", texto: "Córdobas (C$)", simbolo: "C$" },
  { valor: "USD", texto: "Dólares (US$)", simbolo: "US$" },
];

export function montoEnNio(cotizacion) {
  const monto = Number(cotizacion.monto_total);
  if (cotizacion.moneda === "USD") {
    return monto * Number(cotizacion.tipo_cambio || 0);
  }
  return monto;
}

// REGLA: cada monto se muestra SIEMPRE en la moneda de su cotización (US$ o C$), sin
// convertir ni mezclar. Solo cuando hay que juntar cotizaciones de distinta moneda
// (totales de tarjetas) se muestra un monto por moneda.
export const simboloMoneda = (moneda) => (moneda === "USD" ? "US$" : "C$");

export function dineroMoneda(n, moneda) {
  const texto = Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${simboloMoneda(moneda)}${texto}`;
}

// [{ moneda, monto }] -> { NIO: total, USD: total } (solo las monedas que aparecen).
export function sumarPorMoneda(lista) {
  const sumas = {};
  (lista || []).forEach(({ moneda, monto }) => {
    const m = moneda === "USD" ? "USD" : "NIO";
    sumas[m] = (sumas[m] || 0) + Number(monto || 0);
  });
  return sumas;
}

// { USD: 435.8, NIO: 1200 } -> "US$435.80 + C$1,200.00"; sin montos: "C$0.00".
export function textoPorMoneda(sumas, vacio = "C$0.00") {
  const partes = ["USD", "NIO"].filter((m) => sumas && sumas[m] != null).map((m) => dineroMoneda(sumas[m], m));
  return partes.length ? partes.join(" + ") : vacio;
}

export function formatoMonto(cotizacion) {
  return dineroMoneda(cotizacion.monto_total, cotizacion.moneda);
}

export function BadgeFormaPago({ valor }) {
  const info = FORMAS_PAGO.find((f) => f.valor === valor);
  // Siempre se usa con una cotización de por medio: si no trae forma de pago, Pagos la define al gestionar el pago.
  if (!info) {
    return (
      <span
        title="Compras no la definió: la elige Pagos al gestionar el pago"
        style={{
          display: "inline-block",
          padding: "0.15rem 0.6rem",
          borderRadius: 999,
          fontSize: "0.75rem",
          fontWeight: 600,
          color: "#5b6472",
          background: "#eef1f3",
          whiteSpace: "nowrap",
        }}
      >
        Pago por definir
      </span>
    );
  }
  return (
    <span
      style={{
        display: "inline-block",
        padding: "0.15rem 0.6rem",
        borderRadius: 999,
        fontSize: "0.75rem",
        fontWeight: 600,
        color: info.color,
        background: info.bg,
        whiteSpace: "nowrap",
      }}
    >
      {info.texto}
    </span>
  );
}

export function Badge({ estado, mapa }) {
  const info = mapa[estado] || { texto: estado, color: "#5b6472", bg: "#eef1f3" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "0.2rem 0.65rem",
        borderRadius: 999,
        fontSize: "0.78rem",
        fontWeight: 600,
        color: info.color,
        background: info.bg,
        whiteSpace: "nowrap",
      }}
    >
      {info.texto}
    </span>
  );
}
